import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardTitle } from '../components/ui/card';
import {
  ArrowRight,
  Wrench,
  Smartphone,
  Droplets,
  Monitor,
  Battery,
  HardDrive,
  Users,
  Award,
  BookOpen,
} from 'lucide-react';

// Stats data with icons
const stats = [
  { label: 'Devices Repaired', value: 50000, suffix: '+', icon: Wrench },
  { label: 'Happy Customers', value: 20000, suffix: '+', icon: Users },
  { label: 'Students Trained', value: 8000, suffix: '+', icon: BookOpen },
  { label: 'Satisfaction Rate', value: 98, suffix: '%', icon: Award },
];

// Services with icons (using lucide-react icons)
const services = [
  {
    title: 'Board Repairs',
    icon: Monitor,
    description: 'Expert motherboard and logic board diagnostics.',
    link: '/repairs',
  },
  {
    title: 'Screen Repairs',
    icon: Smartphone,
    description: 'Cracked screen? We replace with genuine parts.',
    link: '/repairs',
  },
  {
    title: 'Water Damage',
    icon: Droplets,
    description: 'Fast recovery and ultrasonic cleaning.',
    link: '/repairs',
  },
  {
    title: 'Software Repair',
    icon: Monitor,
    description: 'OS issues, data recovery, and unlocking.',
    link: '/repairs',
  },
  {
    title: 'Battery Replacement',
    icon: Battery,
    description: 'Restore battery life with quality cells.',
    link: '/repairs',
  },
  {
    title: 'Data Recovery',
    icon: HardDrive,
    description: 'Retrieve lost data from damaged drives.',
    link: '/repairs',
  },
];

// Brand logos (using placeholder images; you'll replace with actual SVG files)
const brandLogos = [
  'apple', 'samsung', 'google', 'huawei', 'xiaomi',
  'tecno', 'infinix', 'itel', 'oneplus', 'nokia',
  'dell', 'hp', 'lenovo', 'asus', 'msi',
  'acer', 'microsoft', 'nintendo', 'playstation', 'xbox'
];

export const HomePage: React.FC = () => {
  const statsRef = useRef<HTMLDivElement>(null);

  // Animated counter effect on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const target = parseInt(el.dataset.target || '0', 10);
            const suffix = el.dataset.suffix || '';
            let current = 0;
            const increment = Math.ceil(target / 60);
            const timer = setInterval(() => {
              current += increment;
              if (current >= target) {
                current = target;
                clearInterval(timer);
              }
              el.textContent = current.toLocaleString() + suffix;
            }, 25);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );

    const statNumbers = statsRef.current?.querySelectorAll('.stat-number');
    statNumbers?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-dark to-primary/5 dark:from-dark dark:via-dark-surface dark:to-primary/10 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
                Professional Device Repairs,{' '}
                <span className="text-primary">Online Training</span> &amp; Genuine Gadgets
              </h1>
              <p className="mt-4 text-lg text-[rgb(var(--light-secondary))] dark:text-dark-text">
                Repair. Learn. Shop. Unlock. Grow. – All in one place.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/repairs">
                  <Button variant="primary" size="lg">Book a Repair</Button>
                </Link>
                <Link to="/shop">
                  <Button variant="secondary" size="lg">Shop Devices</Button>
                </Link>
                <Link to="/courses">
                  <Button variant="ghost" size="lg">Browse Courses</Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative h-64 w-64 rounded-full bg-primary/20 p-8 lg:h-80 lg:w-80">
                <img
                  src="/hero-illustration.svg"
                  alt="Repair illustration"
                  className="h-full w-full object-contain"
                />
                <div className="absolute -top-4 -right-4 animate-pulse rounded-full bg-primary p-3">
                  <Wrench className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="container mx-auto -mt-10 px-4">
        <div className="grid grid-cols-2 gap-4 rounded-2xl bg-[rgb(var(--card))] p-6 shadow-md dark:bg-dark-surface md:grid-cols-4">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className="text-center">
                <div className="flex justify-center">
                  <Icon className="h-8 w-8 text-primary" />
                </div>
                <div
                  className="stat-number text-3xl font-bold text-primary md:text-4xl"
                  data-target={stat.value}
                  data-suffix={stat.suffix}
                >
                  0{stat.suffix}
                </div>
                <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="container mx-auto px-4 py-16">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Our Repair Services</h2>
          <p className="mt-2 text-[rgb(var(--light-secondary))]">Expert care for all your devices</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, idx) => {
            const Icon = service.icon;
            return (
              <Card key={idx} className="group transition-all hover:shadow-lg hover:-translate-y-1">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <CardTitle>{service.title}</CardTitle>
                <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">{service.description}</p>
                <Link
                  to={service.link}
                  className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  Learn More <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Brand Carousel */}
      <section className="border-t border-[rgb(var(--border))] py-8">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-[rgb(var(--light-secondary))]">Trusted by leading brands</p>
          <div className="mt-4 overflow-hidden">
            <div className="flex animate-scroll space-x-12 whitespace-nowrap">
              {brandLogos.concat(brandLogos).map((brand, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-center opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0"
                >
                  <img
                    src={`/brands/${brand}.svg`}
                    alt={brand}
                    className="h-8 w-auto"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-16 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold">Ready to get started?</h2>
          <p className="mt-2 text-primary-light">Join thousands of satisfied customers</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button variant="secondary" size="lg" className="bg-white text-primary hover:bg-gray-100">
                Create Account
              </Button>
            </Link>
            <Link to="/repairs">
              <Button variant="ghost" size="lg" className="border border-white text-white hover:bg-white/10">
                Book Repair
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};